<template>
  <div class="dash-card counts-widget">
    <div class="counts-row">
      <div
        v-for="(card, index) in cards"
        :key="card.key"
        class="counts-block"
      >
        <div v-if="index > 0" class="counts-divider" />

        <div class="counts-block-inner">
          <span
            class="counts-icon"
            :style="{ backgroundColor: card.color + '18', color: card.color }"
          >
            <a-icon :type="card.icon" />
          </span>

          <div class="counts-content">
            <div class="counts-label">{{ card.title }}</div>
            <div class="counts-value" :style="{ color: card.color }">
              {{ formatValue(card.value) }}
            </div>
            <div class="counts-subtitle">{{ card.subtitle }}</div>

            <div
              v-if="card.breakdown && card.breakdown.length"
              class="counts-chips"
            >
              <span
                v-for="item in card.breakdown"
                :key="item.label"
                class="counts-chip"
                :style="{ color: item.color, backgroundColor: item.color + '14' }"
              >
                <strong>{{ formatValue(item.value) }}</strong>
                {{ item.label }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "CountsWidget",
  props: {
    // Section `counts` from GET /home
    counts: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    studentInscritCount() {
      const studentCount = Number(this.counts.student_count) || 0;
      const nonInscritCount = Number(this.counts.student_count_non_inscrit) || 0;
      return studentCount - nonInscritCount;
    },
    cards() {
      return [
        {
          key: "activity",
          value: this.counts.activity_count || 0,
          title: this.$t("accueil.activite"),
          subtitle: this.$t("accueil.activiteSubtitle"),
          color: "#52c41a",
          icon: "thunderbolt",
        },
        {
          key: "employees",
          value: this.counts.employee_count || 0,
          title: this.$t("accueil.employees"),
          subtitle: this.$t("accueil.employeesSubtitle"),
          color: "#fa8c16",
          icon: "team",
        },
        {
          key: "classes",
          value: this.counts.classRoom_count || 0,
          title: this.$t("accueil.classes"),
          subtitle: this.$t("accueil.classesSubtitle"),
          color: "#722ed1",
          icon: "home",
        },
        {
          key: "students",
          value: this.counts.student_count || 0,
          title: this.$t("accueil.eleves"),
          subtitle: this.$t("accueil.elevesSubtitle"),
          color: "#1890ff",
          icon: "user",
          breakdown: [
            {
              value: this.studentInscritCount,
              label: this.$t("financialStats.inscrit"),
              color: "#52c41a",
            },
            {
              value: this.counts.student_count_non_inscrit || 0,
              label: this.$t("financialStats.nonInscrit"),
              color: "#ff4d4f",
            },
          ],
        },
      ];
    },
  },
  methods: {
    formatValue(value) {
      if (typeof value === "string") return value;
      if (
        value === null ||
        value === undefined ||
        (typeof value === "number" && isNaN(value))
      ) {
        return "0";
      }
      if (value >= 1000000) return (value / 1000000).toFixed(1) + "M";
      if (value >= 1000) return (value / 1000).toFixed(1) + "K";
      return value.toLocaleString();
    },
  },
};
</script>

<style lang="scss" scoped>
.counts-widget {
  padding: 20px 8px;
}

.counts-row {
  display: flex;
  align-items: stretch;
}

.counts-block {
  flex: 1;
  min-width: 0;
  position: relative;
  display: flex;
}

.counts-divider {
  width: 1px;
  background: var(--dash-border, #f0f0f0);
  flex-shrink: 0;
  align-self: stretch;
  margin: 4px 0;
}

.counts-block-inner {
  flex: 1;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 4px 16px;
  min-width: 0;
}

.counts-icon {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;
}

.counts-content {
  min-width: 0;
}

.counts-label {
  font-size: 0.78rem;
  font-weight: 600;
  color: #8c8c8c;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  margin-bottom: 4px;
}

.counts-value {
  font-size: 1.75rem;
  font-weight: 800;
  line-height: 1.1;
  letter-spacing: -0.02em;
}

.counts-subtitle {
  margin-top: 4px;
  font-size: 0.75rem;
  line-height: 1.35;
  color: #8c8c8c;
}

.counts-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 10px;
}

.counts-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 600;

  strong {
    font-weight: 800;
  }
}

@media (max-width: 1199px) {
  .counts-widget {
    padding: 12px;
  }

  .counts-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .counts-divider {
    display: none;
  }

  .counts-block-inner {
    padding: 12px;
    border-radius: 12px;
    background: #fafafa;
  }
}

@media (max-width: 575px) {
  .counts-row {
    grid-template-columns: 1fr;
  }
}

[data-kit-theme="dark"] {
  .counts-divider {
    background: var(--dash-border, #232135);
  }

  .counts-label,
  .counts-subtitle {
    color: #7575a3;
  }

  @media (max-width: 1199px) {
    .counts-block-inner {
      background: #141322;
    }
  }
}
</style>
